'use client';

import * as React from 'react';
import clsx from 'clsx';
import { IconArrowLeft } from 'icon/Arrows/arrow-left';
import { cn } from '../utils';
import { FocusableLink } from './focusable-link';

export interface PaginationProps {
  total: number;
  current: number;
  calcUrl?: (page: number) => string;
  onChange?: (page: number) => void;
  prevLabel?: string;
  nextLabel?: string;
}

export function Pagination(props: PaginationProps): JSX.Element {
  if (
    process.env.NODE_ENV === 'development' &&
    (props.current > props.total || props.current < 1 || props.total < 1)
  ) {
    throw new Error(`Wrong numbers, ${JSON.stringify(props)}`);
  }
  const currentSiblings = getPaginationSiblings(props.current, props.total);

  function handlePageChange(page: number): void {
    if (props.onChange) {
      props.onChange(page);
    }
  }

  const commonButtonProps = (page: number) => {
    if (props.onChange) {
      return {
        onClick: () => {
          handlePageChange(page);
        },
      };
    }
    if (props.calcUrl) {
      return { href: props.calcUrl(page) };
    }
    return {};
  };

  return (
    <section className="pagination flex items-center justify-between">
      {props.current > 1 ? (
        <NavButton {...commonButtonProps(props.current - 1)}>
          <IconArrowLeft className="size-5 transition-transform group-hover:-translate-x-1" />
          {props.prevLabel || 'Previous'}
        </NavButton>
      ) : (
        <div />
      )}
      <div className="flex gap-1">
        {currentSiblings.map((num) => (
          <NumberButton
            key={num}
            className={cn(
              'pagination-number',
              num === props.current &&
                'rounded-full bg-secondary text-secondary',
            )}
            {...commonButtonProps(num)}
          >
            {num}
          </NumberButton>
        ))}
      </div>
      {props.total > props.current ? (
        <NavButton {...commonButtonProps(props.current + 1)}>
          {props.nextLabel || 'Next'}
          <IconArrowLeft className="size-5 rotate-180 transition-transform group-hover:translate-x-1" />
        </NavButton>
      ) : (
        <div />
      )}
    </section>
  );
}

type PaginationButtonProps = {
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
};

function NumberButton({
  href,
  onClick,
  className,
  children,
}: PaginationButtonProps) {
  const commonClasses = clsx(
    'group inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold text-tertiary transition-colors hover:bg-primary-hover hover:text-secondary-hover disabled:pointer-events-none disabled:opacity-50',
    className,
  );

  if (href) {
    return (
      <FocusableLink className={commonClasses} href={href}>
        {children}
      </FocusableLink>
    );
  }

  return (
    <button
      type="button"
      className={commonClasses}
      onClick={onClick}
      disabled={!onClick}
    >
      {children}
    </button>
  );
}

function NavButton(props: PaginationButtonProps) {
  return (
    <NumberButton
      {...props}
      className={clsx('pagination-nav', props.className, 'border shadow-xs')}
    />
  );
}

// Extract 3 siblings of current number in pagination, e.g. we have 10 pagination, if current number is 5, it should return 4,5,6; If the current number is 1, it should return 1,2,3; If the current number is 10, it should return 8,9,10
function getPaginationSiblings(current: number, total: number) {
  const siblings = [];

  if (current < 3) {
    siblings.push(1, 2, 3);
  } else if (current >= total - 2) {
    for (let i = total - 2; i <= total; i++) {
      siblings.push(i);
    }
  }
  // Otherwise, current number has 3 siblings
  else {
    for (let i = current - 1; i <= current + 1; i++) {
      siblings.push(i);
    }
  }

  return siblings.filter((num) => num >= 1 && num <= total);
}
